/**
 * Страница управления пользователями: таблица, создание, редактирование, удаление, активация
 * Дата: 28.12.2025 - Реализовано для TODO-013 (Frontend страницы + WhatsApp интеграция)
 * Дата: 29.12.2025 - Добавлен TenantSelector для SuperAdmin
 * Дата: 26.01.2026 - Добавлено frontend логирование (TODO-034)
 * Дата: 30.01.2026 - Рефакторинг под унифицированные UI компоненты (TODO-045)
 * Дата: 05.02.2026 - Добавлены поля phone и allow_otp_login (TODO-054)
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - frontend/src/api/users.ts - API для работы с пользователями
 * - frontend/src/hooks/useUsers.ts - хук для управления состоянием пользователей
 * - internal/handler/user.go - бэкенд обработчик пользователей
 * - components/ui/SearchInput.tsx - унифицированный поиск
 * - components/ui/FilterSelect.tsx - унифицированный фильтр
 * - components/ui/StatusBadge.tsx - унифицированный статус
 * - components/ui/EmptyState.tsx - унифицированное пустое состояние
 * - components/ui/LoadingState.tsx - унифицированная загрузка
 * - components/ui/ActionButtons.tsx - унифицированные кнопки действий
 * 
 * КАК РАБОТАЕТ:
 * 1. Загружает список пользователей через useUsers(tenantId)
 * 2. Фильтрует по поисковому запросу и роли
 * 3. Отображает таблицу с действиями (редактирование, активация/деактивация, удаление)
 * 4. Модальные окна для создания/редактирования пользователей
 * 5. Поддержка OTP авторизации через поля phone и allow_otp_login (05.02.2026)
 * 
 * СХЕМА:
 * UsersPage → useUsers → usersApi → backend API
 * UsersPage → SearchInput → фильтрация filteredUsers
 * UsersPage → FilterSelect → фильтрация по роли
 * UsersPage → ActionButtons → handleDelete/handleToggleActive/openEditModal
 * UsersPage → Modal → handleCreate/handleUpdate
 */
import { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { MainLayout } from '@/components/layout/MainLayout';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  Button, 
  Badge, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableHeader, 
  TableCell, 
  Modal, 
  Input, 
  Select, 
  TenantSelector,
  SearchInput,
  FilterSelect,
  StatusBadge,
  EmptyState,
  LoadingState,
  ActionButtons
} from '@/components/ui';
import { useUsers } from '@/hooks/useUsers';
import { useToast } from '@/hooks/useToast';
import { useTenantScope } from '@/hooks/useTenantScope';
import { usePermissions } from '@/hooks/usePermissions';
import { Can } from '@/components/Can';
import { UserPermissionsModal } from '@/components/UserPermissionsModal';
import { Plus, Settings } from 'lucide-react';
import { formatDateTime } from '@/utils/formatters';
import type { CreateUserRequest, UpdateUserRequest } from '@/api/users';
import { rolesApi, Role } from '@/api/roles';

export function UsersPage() {
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();
  const { users, isLoading, createUser, updateUser, deleteUser, toggleActive, isCreating, isUpdating } = useUsers(tenantId);
  const { showSuccess, showError } = useToast();
  const { can } = usePermissions();
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [roles, setRoles] = useState<Role[]>([]);
  
  // User Permissions Modal (31.01.2026)
  const [permissionsModalUser, setPermissionsModalUser] = useState<{id: string, name: string} | null>(null);
  
  // Логирование просмотра страницы
  useEffect(() => {
    logger.info('page_view', { page: 'users' });
  }, []);

  // Загрузить роли при смене тенанта
  // 29.01.2026 - FIX: Передаём tenantId для получения ролей нужного тенанта
  useEffect(() => {
    if (tenantId) {
      rolesApi.list(tenantId).then(setRoles).catch(() => showError('Не удалось загрузить роли'));
    } else {
      setRoles([]); // Сбросить роли если тенант не выбран
    }
  }, [tenantId, showError]);

  // Логирование загрузки пользователей
  useEffect(() => {
    if (!isLoading && users) {
      logger.info('users_list_loaded', { 
        count: users.length,
        tenant_id: tenantId 
      });
    }
  }, [isLoading, users, tenantId]);

  // Форма создания/редактирования
  // Дата: 05.02.2026 - TODO-054: добавлены phone и allow_otp_login
  const [formData, setFormData] = useState<CreateUserRequest & { id?: string }>({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role_id: '',
    phone: '',
    allow_otp_login: false,
  });

  const handleCreate = async () => {
    logger.info('user_create_click', { 
      username: formData.username,
      role_id: formData.role_id 
    });
    try {
      const result = await createUser(formData);
      logger.info('user_created', { 
        user_id: result.id,
        username: result.username,
        role_id: result.role_id 
      });
      showSuccess('Пользователь создан');
      setIsCreateModalOpen(false);
      resetForm();
    } catch (error: any) {
      logger.error('user_create_failed', { 
        error: error.response?.data?.error || error.message,
        username: formData.username 
      });
      showError(error.response?.data?.error || 'Ошибка при создании пользователя');
    }
  };

  const handleUpdate = async () => {
    if (!editingUser) return;
    logger.info('user_update_click', { user_id: editingUser });
    try {
      const updateData: UpdateUserRequest = {
        email: formData.email,
        full_name: formData.full_name,
        role_id: formData.role_id,
        phone: formData.phone, // TODO-054: OTP Login (05.02.2026)
        allow_otp_login: formData.allow_otp_login, // TODO-054: OTP Login (05.02.2026)
      };
      await updateUser({ id: editingUser, data: updateData });
      logger.info('user_updated', { user_id: editingUser });
      showSuccess('Пользователь обновлен');
      setEditingUser(null);
      resetForm();
    } catch (error: any) {
      logger.error('user_update_failed', { 
        user_id: editingUser,
        error: error.response?.data?.error || error.message 
      });
      showError(error.response?.data?.error || 'Ошибка при обновлении пользователя');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Вы уверены, что хотите удалить этого пользователя?')) return;
    logger.info('user_delete_click', { user_id: id });
    try {
      await deleteUser(id);
      logger.info('user_deleted', { user_id: id });
      showSuccess('Пользователь удален');
    } catch (error: any) {
      logger.error('user_delete_failed', { 
        user_id: id,
        error: error.response?.data?.error || error.message 
      });
      showError(error.response?.data?.error || 'Ошибка при удалении пользователя');
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    logger.info('user_toggle_active_click', { user_id: id, is_active: isActive });
    try {
      await toggleActive({ id, isActive });
      logger.info('user_toggle_active_success', { user_id: id, is_active: isActive });
      showSuccess(isActive ? 'Пользователь деактивирован' : 'Пользователь активирован');
    } catch (error: any) {
      logger.error('user_toggle_active_failed', { 
        user_id: id,
        is_active: isActive,
        error: error.response?.data?.error || error.message 
      });
      showError(error.response?.data?.error || 'Ошибка при изменении статуса');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      full_name: '',
      password: '',
      role_id: '',
      phone: '', // TODO-054: OTP Login (05.02.2026)
      allow_otp_login: false, // TODO-054: OTP Login (05.02.2026)
    });
  };

  const openEditModal = (user: typeof users[0]) => {
    setEditingUser(user.id);
    setFormData({
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      password: '',
      role_id: user.role_id,
      phone: user.phone || '', // TODO-054: OTP Login (05.02.2026)
      allow_otp_login: user.allow_otp_login || false, // TODO-054: OTP Login (05.02.2026)
      id: user.id,
    });
  };

  // Фильтрация пользователей
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = !roleFilter || user.role_id === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Уникальные роли для фильтра
  const uniqueRoles = Array.from(new Set(users.map(u => ({ id: u.role_id, name: u.role_name }))))
    .reduce((acc, curr) => {
      if (!acc.find(r => r.id === curr.id)) {
        acc.push(curr);
      }
      return acc;
    }, [] as Array<{ id: string; name: string }>);

  return (
    <MainLayout 
      headerTitle="Пользователи"
      headerActions={
        <Can permission="users:create">
          <Button 
            onClick={() => setIsCreateModalOpen(true)}
            testId="btn-create-user"
            disabled={requireTenantSelection}
          >
            <Plus className="w-4 h-4 mr-2" />
            Добавить пользователя
          </Button>
        </Can>
      }
    >
      <div className="p-6 space-y-6">
        {/* TenantSelector для SuperAdmin */}
        {isSuperAdmin && (
          <TenantSelector
            value={selectedTenantId || ''}
            onChange={setSelectedTenant}
          />
        )}
        
        {/* Фильтры */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Поиск по имени, email или username..."
                  testId="input-search-users"
                />
              </div>
              <div className="w-64">
                <FilterSelect
                  value={roleFilter}
                  onChange={setRoleFilter}
                  options={uniqueRoles.map(r => ({ value: r.id, label: r.name }))}
                  placeholder="Все роли"
                  allOptionLabel="Все роли"
                  testId="select-role-filter"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Таблица пользователей */}
        <Card>
          <CardHeader>
            <CardTitle>Список пользователей ({filteredUsers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {requireTenantSelection ? (
              <EmptyState 
                type="noUsers" 
                title="Выберите клинику"
                description="Выберите клинику для просмотра пользователей"
              />
            ) : isLoading ? (
              <LoadingState />
            ) : filteredUsers.length === 0 ? (
              <EmptyState 
                type="noUsers"
                title={searchQuery || roleFilter ? 'Пользователи не найдены' : 'Нет пользователей'}
                description={searchQuery || roleFilter ? 'Попробуйте изменить параметры поиска' : 'Создайте первого пользователя'}
                actionText="Создать пользователя"
                onAction={() => setIsCreateModalOpen(true)}
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Имя</TableHeader>
                    <TableHeader>Email</TableHeader>
                    <TableHeader>Роль</TableHeader>
                    <TableHeader>Статус</TableHeader>
                    <TableHeader>Создан</TableHeader>
                    <TableHeader>Действия</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id} testId={`row-user-${user.id}`}>
                      <TableCell className="font-medium">{user.full_name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.role_name}</Badge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={user.is_active} />
                      </TableCell>
                      <TableCell>{formatDateTime(user.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <ActionButtons
                            onEdit={can('users:update') ? () => openEditModal(user) : undefined}
                            onToggle={can('users:update') ? () => handleToggleActive(user.id, user.is_active) : undefined}
                            onDelete={can('users:delete') ? () => handleDelete(user.id) : undefined}
                            isActive={user.is_active}
                            testIdPrefix={`user-${user.id}`}
                          />
                          {can('users:write') && (
                            <button
                              onClick={() => setPermissionsModalUser({ id: user.id, name: user.full_name })}
                              className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white"
                              title="Права и IP"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Модалка создания пользователя */}
        <Modal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            resetForm();
          }}
          title="Создать пользователя"
          size="md"
          testId="modal-create-user"
          footer={
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  resetForm();
                }}
              >
                Отмена
              </Button>
              <Button
                onClick={handleCreate}
                disabled={isCreating || !formData.username || !formData.email || !formData.full_name || !formData.password || !formData.role_id}
                testId="btn-submit-create-user"
              >
                {isCreating ? 'Создание...' : 'Создать'}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <Input
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              testId="input-username"
            />
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              testId="input-email"
            />
            <Input
              label="Полное имя"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required
              testId="input-full-name"
            />
            <Input
              label="Пароль"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              testId="input-password"
            />
            <Select
              label="Роль"
              value={formData.role_id}
              onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
              options={[
                { value: '', label: 'Выберите роль' },
                ...roles.map(r => ({ value: r.id, label: r.name }))
              ]}
              required
              testId="select-role"
            />
            
            {/* TODO-054: OTP Login поля (05.02.2026) */}
            <Input
              label="Телефон"
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+79261234567"
              testId="input-phone"
            />
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="allow_otp_login"
                checked={formData.allow_otp_login || false}
                onChange={(e) => setFormData({ ...formData, allow_otp_login: e.target.checked })}
                disabled={!formData.phone}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="allow_otp_login" className="text-sm text-gray-300">
                Разрешить вход по коду (WhatsApp/SMS)
              </label>
            </div>
          </div>
        </Modal>

        {/* Модалка редактирования пользователя */}
        <Modal
          isOpen={!!editingUser}
          onClose={() => {
            setEditingUser(null);
            resetForm();
          }}
          title="Редактировать пользователя"
          size="md"
          testId="modal-edit-user"
          footer={
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditingUser(null);
                  resetForm();
                }}
              >
                Отмена
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={isUpdating || !formData.email || !formData.full_name || !formData.role_id}
                testId="btn-submit-edit-user"
              >
                {isUpdating ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            <Input
              label="Username"
              value={formData.username}
              disabled
              testId="input-username-readonly"
            />
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              testId="input-email-edit"
            />
            <Input
              label="Полное имя"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required
              testId="input-full-name-edit"
            />
            <Select
              label="Роль"
              value={formData.role_id}
              onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
              options={[
                { value: '', label: 'Выберите роль' },
                ...roles.map(r => ({ value: r.id, label: r.name }))
              ]}
              required
              testId="select-role-edit"
            />
            
            {/* TODO-054: OTP Login поля (05.02.2026) */}
            <Input
              label="Телефон"
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+79261234567"
              testId="input-phone-edit"
            />
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="allow_otp_login_edit"
                checked={formData.allow_otp_login || false}
                onChange={(e) => setFormData({ ...formData, allow_otp_login: e.target.checked })}
                disabled={!formData.phone}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="allow_otp_login_edit" className="text-sm text-gray-300">
                Разрешить вход по коду (WhatsApp/SMS)
              </label>
            </div>
          </div>
        </Modal>

        {/* Модалка прав и IP whitelist (31.01.2026) */}
        {permissionsModalUser && (
          <UserPermissionsModal
            isOpen={!!permissionsModalUser}
            onClose={() => setPermissionsModalUser(null)}
            userId={permissionsModalUser.id}
            userName={permissionsModalUser.name}
          />
        )}
      </div>
    </MainLayout>
  );
}
